import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Image as ImageIcon, RefreshCw, Loader2, Search, Copy, Check,
  ChevronLeft, ChevronRight, HardDrive, FolderOpen, Eye, ExternalLink,
} from 'lucide-react';
import { adminAPI } from '@/services/api';
import { toast } from 'sonner';

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const FOLDER_LABELS = {
  speakers: 'Speaker Images',
  webinars: 'Webinar Thumbnails',
  industries: 'Industry Images',
  seminars: 'Seminar Images',
  gallery: 'Gallery',
  documents: 'Documents',
};

export default function AdminMediaLibrary() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderCounts, setFolderCounts] = useState({});
  const [previewAsset, setPreviewAsset] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const perPage = 48;

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (activeFolder) params.folder = activeFolder;
      if (search) params.search = search;
      const response = await adminAPI.get('/media/assets', { params });
      setAssets(response.data.assets || []);
      setTotal(response.data.total || 0);
      setFolderCounts(response.data.folder_counts || {});
    } catch (error) {
      toast.error('Failed to load media assets');
    }
    setLoading(false);
  }, [page, search, activeFolder]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await adminAPI.post('/media/sync');
      const d = response.data;
      toast.success(`Sync complete: ${d.new} new assets, ${d.total} total`);
      fetchAssets();
    } catch (error) {
      toast.error('Failed to sync media');
    }
    setSyncing(false);
  };

  const handleCopyUrl = (asset) => {
    const url = asset.public_url;
    if (url) {
      navigator.clipboard.writeText(url);
      setCopiedId(asset.id);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const totalPages = Math.ceil(total / perPage);
  const totalAssets = Object.values(folderCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="admin-media-library">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-navy-700">Media Library</h2>
          <p className="text-slate-500">All images synced from R2. Auto-updates when new files are uploaded to R2.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchAssets} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleSync} size="sm" disabled={syncing}
            className="bg-navy-600 hover:bg-navy-700" data-testid="sync-media-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HardDrive className="w-4 h-4 mr-2" />}
            Sync from R2
          </Button>
        </div>
      </div>

      {/* Folder Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeFolder === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFolder(null); setPage(1); }}
          className={activeFolder === null ? 'bg-navy-600' : ''}
        >
          All ({totalAssets})
        </Button>
        {Object.entries(FOLDER_LABELS).map(([key, label]) => {
          const count = folderCounts[key] || 0;
          if (count === 0) return null;
          return (
            <Button
              key={key}
              variant={activeFolder === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveFolder(key); setPage(1); }}
              className={activeFolder === key ? 'bg-navy-600' : ''}
            >
              {label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by filename..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-10"
          data-testid="search-media-input"
        />
      </div>

      {/* Assets Grid */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ImageIcon className="w-5 h-5 text-navy-500" />
            {activeFolder ? FOLDER_LABELS[activeFolder] || activeFolder : 'All Assets'} ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16">
              <ImageIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">No media assets found. Click "Sync from R2" to import.</p>
              <Button onClick={handleSync} size="sm">
                <HardDrive className="w-4 h-4 mr-2" /> Sync from R2
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    className="group relative border rounded-lg overflow-hidden bg-slate-50 hover:shadow-md transition-shadow cursor-pointer"
                    data-testid={`media-asset-${asset.id}`}
                  >
                    {/* Image Preview */}
                    <div className="aspect-square relative overflow-hidden bg-slate-100"
                      onClick={() => setPreviewAsset(asset)}>
                      {asset.public_url && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(asset.file_type) ? (
                        <img
                          src={asset.public_url}
                          alt={asset.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-slate-300" />
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button size="sm" variant="secondary" className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); setPreviewAsset(asset); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="secondary" className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); handleCopyUrl(asset); }}>
                          {copiedId === asset.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    {/* Info */}
                    <div className="p-2">
                      <p className="text-xs font-medium truncate" title={asset.filename}>
                        {asset.filename}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {asset.folder}
                        </Badge>
                        <span className="text-[10px] text-slate-400">{formatSize(asset.size)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={!!previewAsset} onOpenChange={() => setPreviewAsset(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="truncate pr-8 text-sm">{previewAsset?.filename}</DialogTitle>
          </DialogHeader>
          {previewAsset && (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center" style={{maxHeight: '250px'}}>
                {previewAsset.public_url && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(previewAsset.file_type) ? (
                  <img src={previewAsset.public_url} alt={previewAsset.filename}
                    className="w-full object-contain" style={{maxHeight: '250px'}} />
                ) : (
                  <div className="py-12 text-center">
                    <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">Preview not available</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-[60px_1fr] gap-x-2 gap-y-1.5 text-xs items-center">
                <span className="font-medium text-slate-500">Folder:</span>
                <Badge variant="outline" className="w-fit text-[10px]">{previewAsset.folder}</Badge>
                <span className="font-medium text-slate-500">Size:</span>
                <span>{formatSize(previewAsset.size)}</span>
                <span className="font-medium text-slate-500">URL:</span>
                <div className="flex items-center gap-1 min-w-0">
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded truncate text-[10px] flex-1 min-w-0">
                    {previewAsset.public_url}
                  </code>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => handleCopyUrl(previewAsset)}>
                    {copiedId === previewAsset.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => window.open(previewAsset.public_url, '_blank')}>
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
                <span className="font-medium text-slate-500">Path:</span>
                <code className="bg-slate-100 px-1.5 py-0.5 rounded truncate text-[10px]">
                  {previewAsset.r2_path}
                </code>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
